import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useGetCountries from 'api/useGetCountries';
import useGetLanguages from 'api/useGetLanguages';
import { HIDDEN_COUNTRIES } from 'pages/country-select/constants';
import { DEFAULT_LANGUAGE, DEFAULT_TRANSLATION_MARKET, ROUTES } from 'shared/constants';
import { SESSION_STORAGE_KEYS } from 'shared/sessionStorageUtils';
import { isLocaleAvailable } from 'shared/utils/isLocaleAvailable';
import { selectIsQueryError } from 'store/calculator/selectors/calculator';
import { useAppSelector } from 'store/hooks';
import useLocalStorage from './useLocalStorage';
import { getLocaleString } from 'shared/utils/getLocaleString';
import { Country } from 'shared/types';

const isRouteAvailable = (path: string) => Object.values(ROUTES).includes(path);

const useBaseUrl = () => {
  const { i18n } = useTranslation();
  const { countries } = useGetCountries();
  const { languages } = useGetLanguages();
  const isQueryError = useAppSelector(selectIsQueryError);
  const [isLoading, setIsLoading] = useState(true);
  const { saveValueToLocalStorage, removeStoredValueByKey } = useLocalStorage();
  const pathname = window.location.pathname;
  const firstPathEl = pathname.split('/')[1];
  const langInBrowser = window.navigator.language;
  const languageOrMarketChangeEvent = new Event('onLanguageOrMarketChange');

  const getCountryByCode = (code: string, countries?: Country[]) =>
    countries?.find((country) => country.code === code);
  
  const clearCountryStorage = () => {
    sessionStorage.clear();
    removeStoredValueByKey(SESSION_STORAGE_KEYS.country);
  };

  const getDefaultBaseURL = () => {
    if (countries && 
        languages && 
        isLocaleAvailable(i18n.language, languages, countries)) {
      return '/' + i18n.language;
    }

    return '';
  };

  const [baseURL, setBaseURL] = useState(getDefaultBaseURL());

  useEffect(() => {
    if (isQueryError) {
      document.dispatchEvent(languageOrMarketChangeEvent);
      setIsLoading(false);
    }
  }, [isQueryError]);

  useEffect(() => {
    let newLang = DEFAULT_LANGUAGE;
    let newMarket = DEFAULT_TRANSLATION_MARKET;
    let defaultMarketSet = true;
    let newBaseURL = '';

    //Languages and countries are loaded
    if (!countries || !languages) {
      setIsLoading(true);
      return;
    }

    // language/locale is present in the url
    if (firstPathEl && isLocaleAvailable(firstPathEl, languages, countries)) {
      // if it's a valid locale it is presented as {languageCode}-{countryCode}
      // in this case we only need the language to be saved
      const firstPathElSplit = firstPathEl.split('-');

      // if there is a dialect added, we need to add the dialect
      newLang = firstPathElSplit.length > 2 ?
                  `${firstPathElSplit[0]}-${firstPathElSplit[1]}`
                  : firstPathElSplit[0];

      newMarket = firstPathElSplit[firstPathElSplit.length -1];
      defaultMarketSet = false;
      newBaseURL = '/' + firstPathEl;
    } else if (
      // language/locale taken from browser
      langInBrowser !== DEFAULT_LANGUAGE &&
      isLocaleAvailable(langInBrowser, languages, countries) &&
      isRouteAvailable(pathname)
    ) {
      const langInBrowserSplit = langInBrowser.split('-');

      newLang = langInBrowser;
      newBaseURL = '/' + langInBrowser;
      newMarket = langInBrowserSplit[langInBrowserSplit.length -1];
      defaultMarketSet = false;
      window.history.replaceState('', '', `${getLocaleString(newLang, newMarket)}${pathname}`);
      document.dispatchEvent(languageOrMarketChangeEvent);
    }
    
    if (HIDDEN_COUNTRIES.includes(newMarket)) {
      clearCountryStorage();
      window.location.replace('/404');
      setIsLoading(false);
      return;
    }

    document.documentElement.lang = newLang;

    if (newLang === DEFAULT_LANGUAGE) {
      document.dispatchEvent(languageOrMarketChangeEvent);
    }

    const newMarketCountry = getCountryByCode(newMarket, countries);

    saveValueToLocalStorage(SESSION_STORAGE_KEYS.language, { value: newLang, default: DEFAULT_LANGUAGE });
    saveValueToLocalStorage(SESSION_STORAGE_KEYS.country, { code: newMarketCountry?.code, name: newMarketCountry?.name, setByDefault: defaultMarketSet });

    i18n.changeLanguage(getLocaleString(newLang,newMarket));
    setBaseURL(newBaseURL);
    setIsLoading(false);
  }, [firstPathEl, langInBrowser, countries, languages]);

  return {
    baseURL,
    isLoading,
  };
};

export default useBaseUrl;
